/**
 * Asset resolution for export. Resolves image sources to local file paths:
 * - CDN URLs (digitaloceanspaces.com) → download via Worker/proxy → assets/images/[hash].[ext]
 * - pb-asset://[id] (user uploads) → extract from IndexedDB → assets/images/[filename]
 * - Relative paths → kept as-is (with warning)
 * - External URLs (other domains) → kept as-is
 *
 * Returns a Map of original URL → { localPath, blob } for resolved assets,
 * plus a list of warnings for unresolved/kept-as-is URLs.
 */

import { store } from '../core/state.js';
import { reportError } from '../core/report-error.js';
import { mapWithConcurrency } from '../shared/concurrency.js';
import { getExportTarget } from './export-targets.js';
import {
  lookupAssetFetch,
  storeAssetFetchHit,
  storeAssetFetchMiss,
  refreshAssetFetch,
  dedupeAssetFetch
} from '../core/asset-fetch-cache.js';
import {
  rewriteCssAssetUrls,
  rewriteCssAssetUrlsPreferVendored,
  getVendoredAssetMapSync
} from '../shared/template-support-css.js';
import { loadAsset, loadJSON } from '../core/asset-loader.js';
import {
  collectVendoredUris,
  parseVendoredUri,
  vendoredFileName
} from '../shared/vendored-asset-uri.js';
import { isStockPhotoUrl } from '../shared/stock-photo-hosts.js';

const PB_ASSET_RE = /pb-asset:\/\/([a-f0-9-]+)/g;
const CSS_URL_RE = /url\((['"]?)([^"')]+)\1\)/gi;

export function isCdnUrl(url) {
  return isStockPhotoUrl(url);
}

function extensionFromUrl(url) {
  try {
    const pathname = new URL(url).pathname;
    const match = pathname.match(/\.([a-zA-Z0-9]+)$/);
    if (match) return match[1].toLowerCase();
  } catch {}
  return 'jpg'; // default for CDN images
}

function extensionFromMime(mime) {
  const map = {
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/gif': 'gif',
    'image/webp': 'webp',
    'image/svg+xml': 'svg',
    'image/avif': 'avif'
  };
  return map[mime] || null;
}

function isLikelyImageUrl(url) {
  try {
    const pathname = new URL(url).pathname.toLowerCase();
    return /\.(jpe?g|png|gif|webp|svg|avif)(?:$|[?#])/.test(pathname);
  } catch {
    return /\.(jpe?g|png|gif|webp|svg|avif)(?:$|[?#])/.test(String(url || '').toLowerCase());
  }
}

/**
 * IF-4 font bucket. Kept separate from `isLikelyImageUrl` because the two ask
 * different questions of the same url(): the image test guards what gets
 * DOWNLOADED from a network, while a font reference in template CSS is
 * resolved from the local vendored store and is emitted under `assets/fonts/`.
 */
export function isLikelyFontUrl(url) {
  const test = /\.(woff2?|ttf|otf|eot)(?:$|[?#])/;
  try {
    return test.test(new URL(url).pathname.toLowerCase());
  } catch {
    return test.test(String(url || '').toLowerCase());
  }
}

function isSkippableUrl(url) {
  if (!url) return true;
  const value = String(url).trim();
  if (!value) return true;
  if (value.startsWith('#')) return true;
  if (value.startsWith('data:')) return true;
  if (value.startsWith('blob:')) return true;
  if (value.startsWith('javascript:')) return true;
  if (value.startsWith('mailto:')) return true;
  if (value.startsWith('tel:')) return true;
  if (value.startsWith('var(')) return true;
  return false;
}

function parseSrcset(value) {
  return String(value || '')
    .split(',')
    .map((candidate) => candidate.trim().split(/\s+/)[0])
    .filter(Boolean);
}

function addCollectedUrl(url, buckets, options = {}) {
  let value = String(url || '').trim();
  if (/^\/(?!\/)/.test(value) && options.sourceDomain) {
    const domain = String(options.sourceDomain)
      .trim()
      .replace(/^https?:\/\//i, '')
      .replace(/\/+$/, '');
    if (domain) value = `https://${domain}${value}`;
  }
  if (isSkippableUrl(value)) return;
  if (value.startsWith('pb-asset://')) return; // already handled by PB_ASSET_RE
  if (value.startsWith('pb-vendored://')) return; // handled by collectVendoredUris
  if (options.requireLikelyImage && !isLikelyImageUrl(value)) return;

  if (isCdnUrl(value)) {
    buckets.cdnUrls.add(value);
  } else if (value.startsWith('http://') || value.startsWith('https://')) {
    buckets.externalUrls.add(value);
  } else {
    buckets.relativeUrls.add(value);
  }
}

function collectPbAssetRefs(text, buckets) {
  if (typeof text !== 'string' || !text) return;
  PB_ASSET_RE.lastIndex = 0;
  for (const match of text.matchAll(PB_ASSET_RE)) {
    buckets.assetIds.add(match[1]);
  }
}

function collectCSSImageUrls(css, buckets) {
  if (typeof css !== 'string' || !css) return;
  collectPbAssetRefs(css, buckets);
  for (const uri of collectVendoredUris(css)) buckets.vendoredUris.add(uri);
  CSS_URL_RE.lastIndex = 0;
  let cssMatch;
  while ((cssMatch = CSS_URL_RE.exec(css)) !== null) {
    const url = (cssMatch[2] || '').trim();
    addCollectedUrl(url, buckets, { requireLikelyImage: true });
  }
}

function collectHTMLDocumentAssetUrls(html, buckets, sourceDomain = '') {
  if (typeof html !== 'string' || !html) return;
  collectPbAssetRefs(html, buckets);
  collectHTMLImageUrls(html, buckets, sourceDomain);

  if (typeof DOMParser === 'undefined') return;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  // Image references in injected document metadata are state-owned too. They
  // do not appear under <img>, but become generated-document dependencies and
  // must share the same adoption/preflight path as visible images.
  for (const link of doc.querySelectorAll('link[href]')) {
    const rel = new Set(
      String(link.getAttribute('rel') || '')
        .toLowerCase()
        .split(/\s+/)
        .filter(Boolean)
    );
    const isImagePreload = rel.has('preload') && link.getAttribute('as')?.toLowerCase() === 'image';
    if (isImagePreload || rel.has('icon') || rel.has('apple-touch-icon')) {
      addCollectedUrl(link.getAttribute('href'), buckets, {
        requireLikelyImage: true,
        sourceDomain
      });
    }
  }
  for (const meta of doc.querySelectorAll('meta[content]')) {
    const key = String(
      meta.getAttribute('property') || meta.getAttribute('name') || ''
    ).toLowerCase();
    if (key === 'og:image' || key === 'twitter:image' || key === 'twitter:image:src') {
      addCollectedUrl(meta.getAttribute('content'), buckets, {
        requireLikelyImage: true,
        sourceDomain
      });
    }
  }
  for (const styleEl of doc.querySelectorAll('style')) {
    const css = sourceDomain
      ? rewriteCssAssetUrls(styleEl.textContent || '', sourceDomain)
      : styleEl.textContent || '';
    collectCSSImageUrls(css, buckets);
  }
  for (const element of doc.querySelectorAll('[style]')) {
    const css = sourceDomain
      ? rewriteCssAssetUrls(element.getAttribute('style') || '', sourceDomain)
      : element.getAttribute('style') || '';
    collectCSSImageUrls(css, buckets);
  }
}

function collectHTMLImageUrls(html, buckets, sourceDomain = '') {
  if (typeof DOMParser === 'undefined') {
    // Best-effort fallback for non-browser test contexts. Runtime export runs
    // in the browser and uses the DOM branch below.
    const imgTagRe = /<(img|source|image|input)\b[^>]*>/gi;
    let tagMatch;
    while ((tagMatch = imgTagRe.exec(html)) !== null) {
      const tag = tagMatch[0];
      if (/^<source\b/i.test(tag) && !/<picture[\s>]/i.test(html)) continue;
      const srcMatch = tag.match(/\s(src|href|xlink:href)\s*=\s*["']([^"']+)["']/i);
      if (srcMatch) addCollectedUrl(srcMatch[2], buckets, { sourceDomain });
      const srcsetMatch = tag.match(/\ssrcset\s*=\s*["']([^"']+)["']/i);
      if (srcsetMatch) {
        for (const candidate of parseSrcset(srcsetMatch[1])) {
          addCollectedUrl(candidate, buckets, { sourceDomain });
        }
      }
    }
    return;
  }

  const doc = new DOMParser().parseFromString(html, 'text/html');

  for (const img of doc.querySelectorAll('img')) {
    addCollectedUrl(img.getAttribute('src'), buckets, { sourceDomain });
    for (const candidate of parseSrcset(img.getAttribute('srcset'))) {
      addCollectedUrl(candidate, buckets, { sourceDomain });
    }
  }

  // <source> is image material only inside <picture>. Video/audio sources are
  // intentionally excluded so embeds and media streams are not downloaded as images.
  for (const source of doc.querySelectorAll('picture source')) {
    addCollectedUrl(source.getAttribute('src'), buckets, { sourceDomain });
    for (const candidate of parseSrcset(source.getAttribute('srcset'))) {
      addCollectedUrl(candidate, buckets, { sourceDomain });
    }
  }

  for (const input of doc.querySelectorAll('input[type="image"]')) {
    addCollectedUrl(input.getAttribute('src'), buckets, { sourceDomain });
  }

  for (const image of doc.querySelectorAll('image')) {
    addCollectedUrl(image.getAttribute('href') || image.getAttribute('xlink:href'), buckets, {
      sourceDomain
    });
  }

  // Imported runtimes frequently defer an image through a data attribute
  // rather than an <img>. These references are just as structural as src=:
  // lite-video posters and JS-applied parallax backgrounds visibly break when
  // their original root-relative URL is left behind.
  const deferredAttrs = [
    'data-src',
    'poster',
    'data-poster',
    'data-placeholder',
    'data-bg',
    'data-bg-webp',
    'data-bg-jpeg'
  ];
  for (const element of doc.querySelectorAll(deferredAttrs.map((attr) => `[${attr}]`).join(','))) {
    for (const attr of deferredAttrs) {
      if (!element.hasAttribute(attr)) continue;
      addCollectedUrl(element.getAttribute(attr), buckets, {
        requireLikelyImage: true,
        sourceDomain
      });
    }
  }
}

function defaultExportAssetProxyEndpoint() {
  const win = globalThis.window;
  const explicit =
    typeof win?.__PB_EXPORT_ASSET_PROXY_ENDPOINT === 'string'
      ? win.__PB_EXPORT_ASSET_PROXY_ENDPOINT.trim()
      : '';
  if (explicit) return explicit;

  const composeEndpoint =
    typeof win?.__PB_COMPOSE_ENDPOINT === 'string' ? win.__PB_COMPOSE_ENDPOINT.trim() : '';
  if (composeEndpoint) {
    try {
      return `${new URL(composeEndpoint).origin}/api/export/assets/fetch`;
    } catch {
      /* fall through */
    }
  }

  return '';
}

async function hashString(str) {
  const data = new TextEncoder().encode(str);
  const buf = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
    .slice(0, 12);
}

/**
 * Collect all image URLs from all section HTML/CSS across all pages.
 *
 * Section and template CSS frequently uses root-relative paths like
 * `url(/assets/images/fin.svg)` that the scraper missed when rewriting
 * HTML img src to absolute URLs. We absolutize them here against the
 * relevant source-template domain so they bucket into externalUrls and
 * get downloaded by the 11ty export path.
 *
 * @param {object} state - The full project state
 * @param {object} [options]
 * @param {string} [options.templateCSS] - Pre-assembled template CSS (from
 *   assembleLiveTemplateCSS) — its url() values are scanned and bucketed too.
 *   Pass the already-rewritten string so its url() paths are absolute.
 */
export function collectImageUrls(state, options = {}) {
  const buckets = {
    cdnUrls: new Set(),
    assetIds: new Set(),
    relativeUrls: new Set(),
    externalUrls: new Set(),
    // IF-4: pb-vendored:// references (today: the template's own @font-face
    // binaries). Resolved from the local reference bundle, never the network.
    vendoredUris: new Set()
  };

  const allSections = [];
  if (state.sharedSections.navigation) allSections.push(state.sharedSections.navigation);
  if (state.sharedSections.footer) allSections.push(state.sharedSections.footer);
  for (const page of state.pages) {
    allSections.push(...page.sections);
  }

  for (const section of allSections) {
    if (section.html) {
      collectHTMLDocumentAssetUrls(
        section.html,
        buckets,
        section.templateSiteName || state.sourceTemplateDomain || ''
      );
    }

    if (section.css) {
      // Absolutize url(/...) paths against the section's source template
      // domain BEFORE scanning, so they bucket into externalUrls and the
      // 11ty export downloader (downloadExternal:true) fetches them locally.
      //
      // MUST MATCH `generators.js` EXACTLY. This pass builds `resolvedMap` and
      // that one rewrites against it, so the two rewriting the same CSS
      // differently means a url() that resolves to nothing in the ZIP. Both
      // call the same function with the same map — which is why the map comes
      // from the SYNC accessor here rather than being awaited: this function is
      // synchronous, both passes run after `assembleLiveTemplateCSS` has warmed
      // the cache, and a cold cache degrades both of them identically.
      const scannableCss = section.templateSiteName
        ? rewriteCssAssetUrlsPreferVendored(
            section.css,
            section.templateSiteName,
            getVendoredAssetMapSync(section.templateSiteName)
          )
        : section.css;

      collectCSSImageUrls(scannableCss, buckets);
    }
  }

  for (const page of state.pages || []) {
    const ogImage = page?.seo?.ogImage || '';
    collectPbAssetRefs(ogImage, buckets);
    addCollectedUrl(ogImage, buckets, { requireLikelyImage: true });
  }

  // PWA app identity. These are the only PROJECT-level image references — every
  // other scan above walks pages or sections — so without this loop they are
  // invisible to resolveAssets(): the blobs never reach resolvedMap, preflight's
  // blocking missing-asset check never fires, and the exported manifest points
  // at images the ZIP does not contain.
  const pwa = state.pwaInstall;
  if (pwa && typeof pwa === 'object') {
    collectPbAssetRefs(pwa.appIcon || '', buckets);
    addCollectedUrl(pwa.appIcon || '', buckets, { requireLikelyImage: true });
    for (const shot of Array.isArray(pwa.screenshots) ? pwa.screenshots : []) {
      collectPbAssetRefs(shot?.asset || '', buckets);
      addCollectedUrl(shot?.asset || '', buckets, { requireLikelyImage: true });
    }
  }

  collectHTMLDocumentAssetUrls(state.headCode || '', buckets);
  collectHTMLDocumentAssetUrls(state.bodyCode || '', buckets);

  // Scan template-level CSS too (the primary template's _globals.css plus any
  // marker-scoped secondary blocks). Caller should pass the live-assembled
  // CSS, which has already had url() paths absolutized via assembleLiveTemplateCSS.
  const tplCSS = typeof options.templateCSS === 'string' ? options.templateCSS : '';
  if (tplCSS) {
    collectCSSImageUrls(tplCSS, buckets);
  }

  return buckets;
}

/**
 * Sign CDN URLs via the Worker /sign endpoint for proxied download.
 * @param {string[]} urls - Array of CDN URLs to sign
 * @param {string} workerBaseUrl - Base URL of the asset proxy Worker
 * @param {string} apiKey - API key for the /sign endpoint
 * @returns {Map<string, {expires: number, sig: string}>}
 */
async function signCdnUrls(urls, workerBaseUrl, apiKey) {
  const signed = new Map();
  if (urls.length === 0) return signed;

  // Batch in groups of 100
  for (let i = 0; i < urls.length; i += 100) {
    const batch = urls.slice(i, i + 100);
    const resp = await fetch(`${workerBaseUrl}/sign`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${apiKey}`
      },
      body: JSON.stringify({ urls: batch })
    });

    if (!resp.ok) {
      reportError('AssetResolver.sign', new Error(`/sign returned ${resp.status}`), {
        user: 'status',
        level: 'warn',
        message: 'Some export images could not be prepared'
      });
      continue;
    }

    const data = await resp.json();
    for (const entry of data.signed) {
      if (entry.error) {
        reportError(
          'AssetResolver.sign',
          new Error(`Sign failed for ${entry.url}: ${entry.error}`),
          {
            user: 'status',
            level: 'warn',
            message: 'Some export images could not be prepared'
          }
        );
        continue;
      }
      signed.set(entry.url, { expires: entry.expires, sig: entry.sig });
    }
  }

  return signed;
}

/**
 * Discriminated download-failure taxonomy (P1a).
 *
 * Before this existed every failure collapsed to the string "download failed
 * after retries", which is why the operator was told THAT an image failed but
 * never WHY — and why a circuit breaker was impossible to write: a 404 and an
 * unreachable host were the same value. Preflight's finding text derives from
 * these, and P1b's negative cache keys on them.
 */
export const FAILURE_REASON = Object.freeze({
  TIMEOUT: 'timeout',
  NETWORK: 'network',
  PROXY: 'proxy',
  HTTP_4XX: 'http-4xx',
  HTTP_5XX: 'http-5xx',
  CORS: 'cors',
  INVALID_MIME: 'invalid-mime',
  ABORTED: 'aborted',
  CIRCUIT_OPEN: 'skipped-circuit-open',
  VENDORED_MISSING: 'vendored-missing',
  VENDORED_MALFORMED: 'vendored-malformed'
});

/** Reasons worth another attempt. A 4xx or a bad MIME will not fix itself. */
const RETRYABLE = new Set([
  FAILURE_REASON.TIMEOUT,
  FAILURE_REASON.NETWORK,
  FAILURE_REASON.HTTP_5XX,
  FAILURE_REASON.PROXY
]);

/**
 * Reasons that are evidence the ORIGIN is unreachable. Deliberately narrow: a
 * 4xx means the host answered, `invalid-mime` means it served something, and
 * `cors`/`proxy` are failures of the path we took rather than of the host — a
 * proxy outage must never trip the source host's breaker.
 */
const CIRCUIT_REASONS = new Set([
  FAILURE_REASON.NETWORK,
  FAILURE_REASON.TIMEOUT,
  FAILURE_REASON.HTTP_5XX
]);

/** Most-actionable-first, for collapsing a multi-transport chain to one reason. */
const REASON_PRECEDENCE = [
  FAILURE_REASON.ABORTED,
  FAILURE_REASON.HTTP_4XX,
  FAILURE_REASON.INVALID_MIME,
  FAILURE_REASON.HTTP_5XX,
  FAILURE_REASON.TIMEOUT,
  FAILURE_REASON.CORS,
  FAILURE_REASON.PROXY,
  FAILURE_REASON.NETWORK
];

function bestReason(reasons) {
  for (const r of REASON_PRECEDENCE) if (reasons.includes(r)) return r;
  return reasons[0] || FAILURE_REASON.NETWORK;
}

const ok = (blob, headers = null) => ({ ok: true, blob, headers, reasons: [] });

/** Safely read the caching validators off a Response. Fakes may omit headers. */
function readHeaders(resp) {
  const h = resp && resp.headers;
  if (!h || typeof h.get !== 'function') return {};
  return {
    etag: h.get('etag') || null,
    lastModified: h.get('last-modified') || null,
    cacheControl: h.get('cache-control') || null
  };
}
const fail = (reason) => ({ ok: false, blob: null, reasons: [reason] });

function isAbortError(err) {
  return err && (err.name === 'AbortError' || err.name === 'TimeoutError');
}

function throwIfAborted(signal) {
  if (signal?.aborted) throw new DOMException('Asset resolve aborted', 'AbortError');
}

/** Host of a URL, lowercased. '' when unparseable (never groups with a real host). */
function hostOfUrl(url) {
  try {
    const s = String(url || '');
    return new URL(s.startsWith('//') ? `https:${s}` : s).host.toLowerCase();
  } catch {
    return '';
  }
}

/** Classify a thrown fetch rejection. Browsers report CORS as a bare TypeError. */
function classifyThrown(err, { opaqueTypeError = FAILURE_REASON.NETWORK } = {}) {
  if (!err) return FAILURE_REASON.NETWORK;
  if (err.name === 'TimeoutError') return FAILURE_REASON.TIMEOUT;
  if (err.name === 'AbortError') return FAILURE_REASON.ABORTED;
  if (err.name === 'TypeError') {
    // Fetch deliberately hides CORS details from script. Chrome's "Failed to
    // fetch" and Safari's "Load failed" are opaque TypeErrors, so message
    // sniffing cannot distinguish them from the network. A DIRECT external
    // fetch treats that opaque shape as CORS: opening the origin circuit would
    // otherwise skip later URLs that may have a valid CORS policy.
    if (/cors|cross-origin/i.test(String(err.message || ''))) return FAILURE_REASON.CORS;
    return opaqueTypeError;
  }
  return FAILURE_REASON.NETWORK;
}

function classifyStatus(status) {
  if (status >= 500) return FAILURE_REASON.HTTP_5XX;
  if (status >= 400) return FAILURE_REASON.HTTP_4XX;
  return FAILURE_REASON.NETWORK;
}

/**
 * Combine the caller's cancellation with a per-request deadline. Both matter and
 * they are different failures: an abort is the operator, a timeout is the network.
 * `AbortSignal.any`/`.timeout` are recent, so both have a manual fallback.
 */
function requestSignal(signal, timeoutMs) {
  const parts = [];
  if (signal) parts.push(signal);
  if (timeoutMs > 0) {
    if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function') {
      parts.push(AbortSignal.timeout(timeoutMs));
    } else {
      const ctrl = new AbortController();
      setTimeout(() => ctrl.abort(new DOMException('Timed out', 'TimeoutError')), timeoutMs);
      parts.push(ctrl.signal);
    }
  }
  if (parts.length === 0) return undefined;
  if (parts.length === 1) return parts[0];
  if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.any === 'function') {
    return AbortSignal.any(parts);
  }
  const ctrl = new AbortController();
  for (const p of parts) {
    if (p.aborted) {
      ctrl.abort(p.reason);
      break;
    }
    p.addEventListener('abort', () => ctrl.abort(p.reason), { once: true });
  }
  return ctrl.signal;
}

/**
 * Sleep that actually stops. The bare setTimeout this replaces meant an abort
 * during the 1.5s retry backoff was ignored until the timer elapsed — on a dead
 * host that was most of the wall-clock the operator was trying to cancel.
 */
function abortableSleep(ms, signal) {
  if (!(ms > 0)) return Promise.resolve();
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Asset resolve aborted', 'AbortError'));
      return;
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener?.('abort', onAbort);
      resolve();
    }, ms);
    function onAbort() {
      clearTimeout(timer);
      reject(new DOMException('Asset resolve aborted', 'AbortError'));
    }
    signal?.addEventListener?.('abort', onAbort, { once: true });
  });
}

/**
 * Download a CDN image via the Worker proxy.
 * @returns {{ok: boolean, blob: Blob|null, reasons: string[]}}
 */
async function downloadViaCdnProxy(url, workerBaseUrl, signData, reqOpts = {}) {
  const proxyUrl =
    `${workerBaseUrl}/proxy?` +
    new URLSearchParams({
      url,
      expires: String(signData.expires),
      sig: signData.sig
    });

  try {
    const resp = await fetch(proxyUrl, {
      signal: requestSignal(reqOpts.signal, reqOpts.timeoutMs)
    });
    if (!resp.ok) {
      reportError(
        'AssetResolver.proxy',
        new Error(`Proxy fetch failed for ${url}: ${resp.status}`),
        {
          user: 'status',
          level: 'warn',
          message: 'An export image could not be downloaded'
        }
      );
      // A proxy that answers with an error is a PROXY failure, not evidence
      // about the origin — see CIRCUIT_REASONS.
      return fail(resp.status >= 500 ? FAILURE_REASON.PROXY : classifyStatus(resp.status));
    }
    return ok(await resp.blob(), readHeaders(resp));
  } catch (err) {
    if (isAbortError(err) && reqOpts.signal?.aborted) throw err;
    reportError('AssetResolver.proxy', err, {
      user: 'status',
      level: 'warn',
      message: 'An export image could not be downloaded',
      detail: `Proxy fetch error for ${url}: ${err?.message || err}`
    });
    const reason = classifyThrown(err);
    return fail(reason === FAILURE_REASON.NETWORK ? FAILURE_REASON.PROXY : reason);
  }
}

async function downloadViaExportAssetProxy(url, assetProxyEndpoint, reqOpts = {}) {
  if (!assetProxyEndpoint) return null;
  const headers = { 'Content-Type': 'application/json' };

  try {
    const resp = await fetch(assetProxyEndpoint, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify({ url }),
      signal: requestSignal(reqOpts.signal, reqOpts.timeoutMs)
    });
    if (!resp.ok) {
      reportError(
        'AssetResolver.exportProxy',
        new Error(`Export asset proxy failed for ${url}: ${resp.status}`),
        {
          user: 'status',
          level: 'warn',
          message: 'An export image could not be downloaded'
        }
      );
      return fail(resp.status >= 500 ? FAILURE_REASON.PROXY : classifyStatus(resp.status));
    }
    return ok(await resp.blob(), readHeaders(resp));
  } catch (err) {
    if (isAbortError(err) && reqOpts.signal?.aborted) throw err;
    reportError('AssetResolver.exportProxy', err, {
      user: 'status',
      level: 'warn',
      message: 'An export image could not be downloaded',
      detail: `Export asset proxy error for ${url}: ${err?.message || err}`
    });
    const reason = classifyThrown(err);
    return fail(reason === FAILURE_REASON.NETWORK ? FAILURE_REASON.PROXY : reason);
  }
}

/**
 * Download a CDN image directly (fallback when no Worker is configured).
 * May fail due to CORS — that's expected.
 * @returns {{ok: boolean, blob: Blob|null, reasons: string[]}}
 */
async function downloadDirect(url, reqOpts = {}) {
  try {
    const headers = {};
    if (reqOpts.ifNoneMatch) headers['If-None-Match'] = reqOpts.ifNoneMatch;
    if (reqOpts.ifModifiedSince) headers['If-Modified-Since'] = reqOpts.ifModifiedSince;
    const resp = await fetch(url, {
      mode: 'cors',
      ...(Object.keys(headers).length ? { headers } : {}),
      signal: requestSignal(reqOpts.signal, reqOpts.timeoutMs)
    });
    // 304: the cached body is still good. Never a failure.
    if (resp.status === 304) {
      return { ok: true, blob: null, notModified: true, headers: readHeaders(resp), reasons: [] };
    }
    if (!resp.ok) return fail(classifyStatus(resp.status));
    return ok(await resp.blob(), readHeaders(resp));
  } catch (err) {
    if (isAbortError(err) && reqOpts.signal?.aborted) throw err;
    reportError('AssetResolver.directDownload', err, {
      user: 'silent',
      level: 'warn',
      detail: `Direct export image download failed for ${url}: ${err?.message || err}`
    });
    return fail(classifyThrown(err, { opaqueTypeError: FAILURE_REASON.CORS }));
  }
}

/**
 * Per-host circuit breaker. Opens after N consecutive ORIGIN-unreachable
 * failures with no success, so a template domain that has gone offline costs
 * seconds instead of (measured at P0) ~2s per image — 24 minutes across 714.
 * Deliberately narrow: see CIRCUIT_REASONS.
 */
class HostCircuit {
  constructor(threshold) {
    this._threshold = threshold;
    this._streak = new Map();
    this._open = new Set();
  }
  isOpen(host) {
    return this._open.has(host);
  }
  recordSuccess(host) {
    this._streak.set(host, 0);
  }
  /** @param {{reasons: string[], reason: string}} outcome */
  recordFailure(host, outcome) {
    const counts =
      CIRCUIT_REASONS.has(outcome.reason) &&
      !outcome.reasons.includes(FAILURE_REASON.PROXY) &&
      !outcome.reasons.includes(FAILURE_REASON.CORS);
    if (!counts) return;
    const n = (this._streak.get(host) || 0) + 1;
    this._streak.set(host, n);
    if (this._threshold > 0 && n >= this._threshold) this._open.add(host);
  }
}

// Per-export cache of each domain's reference-bundle manifest (null = no bundle).
const _vendoredManifestCache = new Map();

async function loadVendoredManifest(domain) {
  if (_vendoredManifestCache.has(domain)) return _vendoredManifestCache.get(domain);
  let manifest = null;
  try {
    manifest = await loadJSON(`data/reference-bundles/template-websites/${domain}/_manifest.json`);
  } catch {
    manifest = null;
  }
  _vendoredManifestCache.set(domain, manifest);
  return manifest;
}

/** Reset the vendored-manifest cache (test isolation). */
export function _resetVendoredStoreCache() {
  _vendoredManifestCache.clear();
}

function normalizeVendoredHost(value) {
  const s = String(value || '')
    .trim()
    .toLowerCase();
  if (!s || s.startsWith('custom:')) return '';
  try {
    return new URL(s.includes('://') ? s : `https://${s}`).host;
  } catch {
    return s.replace(/^\/+|\/+$/g, '');
  }
}

function vendoredHostAliases(host) {
  const h = String(host || '')
    .trim()
    .toLowerCase();
  if (!h) return [];
  return h.startsWith('www.') ? [h, h.slice(4)] : [h, `www.${h}`];
}

function urlWithHost(url, host) {
  try {
    const parsed = new URL(url);
    parsed.host = host;
    return parsed.href;
  } catch {
    return '';
  }
}

/**
 * Candidate template domains whose assets may be vendored locally. Only these
 * hosts are probed (avoids a manifest fetch for every arbitrary CDN/external URL).
 */
function collectVendoredDomains(state, extra) {
  const set = new Set();
  const host = normalizeVendoredHost(state && state.sourceTemplateDomain);
  for (const alias of vendoredHostAliases(host)) set.add(alias);
  for (const d of extra || []) {
    const h = normalizeVendoredHost(d);
    for (const alias of vendoredHostAliases(h)) set.add(alias);
  }
  return set;
}

/**
 * Look up a URL in the local vendored store (built at ingestion by
 * vendor-template-assets.js). Returns the vendored Blob, or null if the host is
 * not a known vendored domain, has no reference bundle, or the asset was not
 * vendored — in which case the caller falls through to the network path.
 */
async function loadFromVendoredStore(url, vendoredDomains) {
  let domain = '';
  try {
    domain = new URL(url).host.toLowerCase();
  } catch {
    return null;
  }
  if (!domain || !vendoredDomains || !vendoredDomains.has(domain)) return null;
  const domains = vendoredHostAliases(domain).filter((host) => vendoredDomains.has(host));
  const keys = [url, ...domains.map((host) => urlWithHost(url, host)).filter(Boolean)];
  for (const manifestDomain of domains) {
    const manifest = await loadVendoredManifest(manifestDomain);
    const assets = manifest && manifest.vendoredAssets;
    if (!assets) continue;
    const rel = keys.map((key) => assets[key]).find(Boolean);
    if (!rel) continue;
    try {
      const resp = await loadAsset(
        `data/reference-bundles/template-websites/${manifestDomain}/${rel}`
      );
      const blob = await resp.blob();
      if (blob && blob.size > 0) return blob;
    } catch {
      /* try the next host alias */
    }
  }
  return null;
}

/**
 * Run a transport chain with bounded retry. Network resolution is the last
 * resort (after IndexedDB + the local vendored store), so TRANSIENT failures
 * get a couple of backed-off retries before the URL is recorded as a hard
 * failure that blocks the export.
 *
 * Two things this does that the pre-P1a version did not: it stops retrying a
 * reason that cannot improve (a 404 was retried three times, at full backoff),
 * and its backoff is cancellable.
 *
 * @param {() => Promise<{ok:boolean, blob:Blob|null, reasons:string[]}>} attemptFn
 * @param {number[]} retryDelaysMs - delay before each retry (length = retry count)
 * @param {AbortSignal} [signal]
 * @returns {Promise<{ok:boolean, blob:Blob|null, reason:string, reasons:string[], attempts:number}>}
 */
async function downloadWithRetry(attemptFn, retryDelaysMs, signal = null) {
  const delays = Array.isArray(retryDelaysMs) ? retryDelaysMs : [];
  const seen = [];
  let attempts = 0;
  for (let attempt = 0; attempt <= delays.length; attempt += 1) {
    if (attempt > 0) await abortableSleep(delays[attempt - 1], signal);
    attempts += 1;
    const out = await attemptFn();
    if (out.ok) return { ...out, reason: null, attempts };
    seen.push(...out.reasons);
    const reason = bestReason(out.reasons);
    if (!RETRYABLE.has(reason)) {
      return { ok: false, blob: null, reason, reasons: [...new Set(seen)], attempts };
    }
  }
  return {
    ok: false,
    blob: null,
    reason: bestReason(seen),
    reasons: [...new Set(seen)],
    attempts
  };
}

/**
 * Run one URL through the transport chain: worker proxy → export proxy →
 * direct. Collapses the chain's reasons so the caller sees one actionable
 * reason but the circuit breaker can still tell a proxy/CORS failure apart
 * from an unreachable origin.
 */
async function runTransportChain(transports) {
  const reasons = [];
  for (const t of transports) {
    const out = await t();
    if (out === null) continue; // transport not configured
    if (out.ok) {
      return {
        ok: true,
        blob: out.blob,
        notModified: !!out.notModified,
        headers: out.headers || null,
        reasons
      };
    }
    reasons.push(...out.reasons);
  }
  return { ok: false, blob: null, reasons };
}

/**
 * One URL through cache → network → cache (P1b), wrapped in in-flight dedup.
 *
 * Module scope and exported since P2: asset adoption pulls the same bytes the
 * exporter does, and a second download path would mean two retry policies, two
 * cache contracts and two sets of bugs.
 * Returns the P1a outcome shape, so every caller downstream is unchanged.
 *
 * Ordering matters: the local vendored store is consulted by the caller BEFORE
 * this, because it is a project-local file that never expires and costs no
 * network. This layer is only about somebody else's bytes.
 */
/**
 * The standard transport chain for an external (non-CDN) image: server-side
 * proxy first, then a direct CORS fetch. Exported so asset adoption pulls bytes
 * through exactly the transports the exporter uses.
 */
export function externalImageTransports(
  url,
  assetProxyEndpoint = defaultExportAssetProxyEndpoint()
) {
  return (o) => [
    () => downloadViaExportAssetProxy(url, assetProxyEndpoint, o),
    () => downloadDirect(url, o)
  ];
}

export async function fetchAssetThroughCache(
  url,
  buildTransports,
  reqOpts,
  { useCache = true, cacheFailures = true, retryDelaysMs = [500, 1500], signal = null } = {}
) {
  if (!useCache) {
    return downloadWithRetry(
      () => runTransportChain(buildTransports(reqOpts)),
      retryDelaysMs,
      signal
    );
  }
  return dedupeAssetFetch(url, async () => {
    const cached = await lookupAssetFetch(url);
    if (cached.status === 'hit') {
      return { ok: true, blob: cached.blob, reason: null, reasons: [], fromCache: true };
    }
    if (cached.status === 'miss') {
      // A remembered failure inside its TTL. Not re-attempted; still reported.
      return { ok: false, blob: null, reason: cached.reason, reasons: [cached.reason] };
    }
    const conditional =
      cached.status === 'stale'
        ? {
            ...reqOpts,
            ifNoneMatch: cached.meta.etag || undefined,
            ifModifiedSince: cached.meta.lastModified || undefined
          }
        : reqOpts;
    const out = await downloadWithRetry(
      () => runTransportChain(buildTransports(conditional)),
      retryDelaysMs,
      signal
    );
    if (out.ok && out.notModified) {
      const blob = await refreshAssetFetch(url, out.headers || {});
      if (blob) return { ...out, blob, revalidated: true };
      // The validator said unchanged but the body is gone (swept between the
      // lookup and now). Fall through to an unconditional fetch rather than
      // returning a null blob.
      const retry = await downloadWithRetry(
        () => runTransportChain(buildTransports(reqOpts)),
        retryDelaysMs,
        signal
      );
      if (retry.ok) await storeAssetFetchHit(url, retry.blob, retry.headers || {});
      return retry;
    }
    if (out.ok) {
      await storeAssetFetchHit(url, out.blob, out.headers || {});
    } else if (cacheFailures) {
      await storeAssetFetchMiss(url, out.reason);
    }
    return out;
  });
}

/**
 * Resolve all assets for export.
 *
 * @param {object} state - The full project state
 * @param {object} [options]
 * @param {string} [options.workerBaseUrl] - Base URL of the asset proxy Worker (optional)
 * @param {string} [options.apiKey] - API key for the Worker /sign endpoint (optional)
 * @param {string} [options.assetProxyEndpoint] - POST endpoint that downloads external assets server-side.
 * @param {function} [options.onProgress] - Progress callback: ({ total, completed, current })
 * @param {string} [options.target='flat'] - Export target id (see export-targets.js). Decides
 *   where each asset lands in the emitted tree and how HTML refers to it: 'flat' keeps ZIP path
 *   and URL identical, 'eleventy' prefixes 'src/' and serves from '/assets/images/...', 'astro'
 *   splits processed images into src/ from verbatim copies in public/.
 * @param {boolean} [options.downloadExternal=false] - Download non-CDN external image URLs when possible.
 * @returns {{ resolvedMap: Map<string, {localPath: string, htmlPath: string, blob: Blob}>, warnings: string[] }}
 */
export async function resolveAssets(state, options = {}) {
  const {
    workerBaseUrl,
    apiKey,
    onProgress,
    target = 'flat',
    downloadExternal = false,
    templateCSS = '',
    assetProxyEndpoint = defaultExportAssetProxyEndpoint(),
    retryDelaysMs = [500, 1500],
    // Migration 006 (visual diff): where pb-asset:// ids resolve FROM.
    // Defaults to the live IndexedDB — but an OLD version's assets are
    // provably GONE from there (loadFromZip GC's unreferenced blobs), so a
    // version render must inject the bundle's own zip-backed source
    // ({get(id) → {blob, mime, filename}|null}, from readProjectBundle).
    assetSource = null,
    // Cancels the resolve AND every in-flight request (P1a). Before P1a the
    // exporters never passed one, so every abort check in here was dead code
    // and Cancel could not interrupt the phase that consumed the whole wait.
    signal = null,
    // P1a. The pre-P1a loops were strictly serial (measured max in-flight = 1),
    // which is the whole of the "it hangs" complaint.
    concurrency = 6,
    // Per-request deadline, distinct from cancellation: a timeout is the
    // network giving up, an abort is the operator.
    requestTimeoutMs = 20000,
    // Consecutive origin-unreachable failures on one host before the rest of
    // that host's URLs are skipped. 0 disables.
    circuitThreshold = 5,
    // P1b. Off makes a run pure-transport, which is what the P1a suite wants.
    useCache = true
  } = options;
  const targetDescriptor = getExportTarget(target);
  const { cdnUrls, assetIds, relativeUrls, externalUrls, vendoredUris } = collectImageUrls(state, {
    templateCSS
  });
  // Hosts whose assets may have been vendored locally at ingestion. Only these
  // are probed so arbitrary CDN/external URLs don't trigger a manifest fetch.
  const vendoredDomains = collectVendoredDomains(state, options.vendoredDomains);

  const resolvedMap = new Map(); // originalUrl → { localPath, htmlPath, blob }
  const warnings = [];
  // Structured, machine-readable download failures (additive; warnings kept).
  // Fed into the export preflight → BLOCKING `asset-download-failed`, grouped by
  // host so a whole hotlinked site collapses to one cluster finding.
  const failures = []; // { url, kind, reason }

  /** Create a resolved entry with both ZIP path and HTML-reference path. */
  function makeEntry(assetPath, blob, opts = {}) {
    return {
      ...targetDescriptor.placeAsset(assetPath),
      blob,
      kind: opts.kind || 'cdn'
    };
  }
  const total =
    cdnUrls.size + assetIds.size + vendoredUris.size + (downloadExternal ? externalUrls.size : 0);
  let completed = 0;

  function progress(current) {
    if (onProgress) onProgress({ total, completed, current });
  }

  const circuit = new HostCircuit(circuitThreshold);

  const fetchThroughCache = (url, buildTransports, reqOpts) =>
    fetchAssetThroughCache(url, buildTransports, reqOpts, {
      useCache,
      retryDelaysMs,
      signal
    });

  /**
   * Fold a pool's index-addressed results into resolvedMap/failures/warnings.
   * Runs AFTER the pool so mutation order follows input order, not completion
   * order — the other half of the determinism guarantee.
   */
  function foldResults(results, label) {
    for (const r of results) {
      if (!r) continue;
      completed++;
      progress(r.url);
      if (r.ok) {
        resolvedMap.set(r.url, r.entry);
        continue;
      }
      if (r.reason === FAILURE_REASON.CIRCUIT_OPEN) {
        warnings.push(`Skipped ${label} image (host unreachable): ${r.url}`);
      } else {
        warnings.push(`Could not download ${label} image: ${r.url} — keeping original URL`);
      }
      failures.push({ url: r.url, kind: r.kind, reason: r.reason });
    }
  }

  // 1. Resolve pb-asset:// IDs (injected source, else live IndexedDB)
  for (const id of assetIds) {
    throwIfAborted(signal);
    const key = `pb-asset://${id}`;
    try {
      const record = assetSource ? await assetSource.get(id) : await store.assets.get(id);
      if (!record) {
        warnings.push(`Asset ${id} not found in IndexedDB — skipping`);
        completed++;
        progress(key);
        continue;
      }
      const ext = extensionFromMime(record.mime) || extensionFromUrl(record.filename || '');
      const filename =
        record.filename && record.filename !== 'untitled'
          ? record.filename.replace(/[^a-zA-Z0-9._-]/g, '_')
          : `${id.slice(0, 8)}.${ext}`;
      const assetPath = `assets/images/${filename}`;
      const entry = makeEntry(assetPath, record.blob, { kind: 'asset' });

      // Deduplicate: if filename already used, add hash prefix
      if ([...resolvedMap.values()].some((v) => v.localPath === entry.localPath)) {
        const dedupAssetPath = `assets/images/${id.slice(0, 8)}-${filename}`;
        resolvedMap.set(key, makeEntry(dedupAssetPath, record.blob, { kind: 'asset' }));
      } else {
        resolvedMap.set(key, entry);
      }
    } catch (err) {
      warnings.push(`Failed to read asset ${id}: ${err.message}`);
    }
    completed++;
    progress(key);
  }

  // 1.5 Resolve pb-vendored:// references (IF-4). These come from the local
  // reference bundle, so there is no network path and no retry: a miss is a
  // hard failure that blocks the export, because the alternative is shipping a
  // stylesheet whose @font-face points at a file that is not in the ZIP.
  for (const uri of vendoredUris) {
    throwIfAborted(signal);
    const parsed = parseVendoredUri(uri);
    if (!parsed) {
      warnings.push(`Malformed vendored reference kept as-is: ${uri}`);
      failures.push({
        url: uri,
        kind: 'vendored',
        reason: FAILURE_REASON.VENDORED_MALFORMED
      });
      completed++;
      progress(uri);
      continue;
    }
    let blob = null;
    try {
      const resp = await loadAsset(parsed.logicalKey);
      const candidate = await resp.blob();
      if (candidate && candidate.size > 0) blob = candidate;
    } catch (err) {
      warnings.push(`Could not read vendored asset ${uri}: ${err.message || err}`);
    }
    if (blob) {
      const dir = isLikelyFontUrl(parsed.relPath) ? 'assets/fonts' : 'assets/images';
      resolvedMap.set(
        uri,
        makeEntry(`${dir}/${vendoredFileName(uri)}`, blob, { kind: 'vendored' })
      );
    } else {
      failures.push({ url: uri, kind: 'vendored', reason: FAILURE_REASON.VENDORED_MISSING });
    }
    completed++;
    progress(uri);
  }

  // 2. Resolve CDN URLs
  if (cdnUrls.size > 0) {
    const cdnUrlArray = [...cdnUrls];

    // Sign URLs if Worker is available
    let signedMap = new Map();
    if (workerBaseUrl && apiKey) {
      try {
        signedMap = await signCdnUrls(cdnUrlArray, workerBaseUrl, apiKey);
      } catch (err) {
        warnings.push(`Failed to sign CDN URLs: ${err.message}. Attempting direct download.`);
      }
    }

    const cdnResults = await mapWithConcurrency(cdnUrlArray, concurrency, async (url) => {
      throwIfAborted(signal);
      const host = hostOfUrl(url);
      if (circuit.isOpen(host)) {
        return { url, kind: 'cdn', ok: false, reason: FAILURE_REASON.CIRCUIT_OPEN };
      }
      // Resolution order: (2) local vendored store, then (3) network last resort.
      const signData = signedMap.get(url);
      const vendored = await loadFromVendoredStore(url, vendoredDomains);
      const reqOpts = { signal, timeoutMs: requestTimeoutMs };
      const out = vendored
        ? { ok: true, blob: vendored, reasons: [], fromVendored: true }
        : await fetchThroughCache(
            url,
            (o) => [
              () =>
                signData && workerBaseUrl
                  ? downloadViaCdnProxy(url, workerBaseUrl, signData, o)
                  : null,
              () => downloadViaExportAssetProxy(url, assetProxyEndpoint, o),
              () => downloadDirect(url, o)
            ],
            reqOpts
          );
      if (out.ok) {
        circuit.recordSuccess(host);
        const ext = extensionFromUrl(url);
        const hash = await hashString(url);
        return {
          url,
          kind: 'cdn',
          ok: true,
          entry: makeEntry(`assets/images/${hash}.${ext}`, out.blob, {
            kind: vendored ? 'vendored' : 'cdn'
          })
        };
      }
      circuit.recordFailure(host, out);
      return { url, kind: 'cdn', ok: false, reason: out.reason };
    });
    foldResults(cdnResults, 'CDN');
  }

  // 3. Resolve external URLs when requested by 11ty export.
  if (downloadExternal && externalUrls.size > 0) {
    const externalResults = await mapWithConcurrency(
      [...externalUrls],
      concurrency,
      async (url) => {
        throwIfAborted(signal);
        const host = hostOfUrl(url);
        if (circuit.isOpen(host)) {
          return { url, kind: 'external', ok: false, reason: FAILURE_REASON.CIRCUIT_OPEN };
        }
        const vendored = await loadFromVendoredStore(url, vendoredDomains);
        const reqOpts = { signal, timeoutMs: requestTimeoutMs };
        const out = vendored
          ? { ok: true, blob: vendored, reasons: [] }
          : await fetchThroughCache(
              url,
              (o) => [
                () => downloadViaExportAssetProxy(url, assetProxyEndpoint, o),
                () => downloadDirect(url, o)
              ],
              reqOpts
            );
        const blob = out.ok ? out.blob : null;
        if (
          blob &&
          blob.size > 0 &&
          ((blob.type || '').startsWith('image/') || isLikelyImageUrl(url))
        ) {
          circuit.recordSuccess(host);
          const ext = extensionFromMime(blob.type) || extensionFromUrl(url);
          const hash = await hashString(url);
          return {
            url,
            kind: 'external',
            ok: true,
            entry: makeEntry(`assets/images/${hash}.${ext}`, blob, {
              kind: vendored ? 'vendored' : 'external'
            })
          };
        }
        if (blob) {
          // The host answered with something that is not an image. That is not
          // evidence the host is down, so it must not feed the breaker.
          circuit.recordSuccess(host);
          return { url, kind: 'external', ok: false, reason: FAILURE_REASON.INVALID_MIME };
        }
        circuit.recordFailure(host, out);
        return { url, kind: 'external', ok: false, reason: out.reason };
      }
    );
    foldResults(externalResults, 'external');
  }

  // 4. Log warnings for relative and unresolved external URLs (kept as-is)
  for (const url of relativeUrls) {
    throwIfAborted(signal);
    warnings.push(`Relative URL kept as-is: ${url}`);
  }
  if (!downloadExternal) {
    for (const url of externalUrls) {
      throwIfAborted(signal);
      warnings.push(`External URL kept as-is: ${url}`);
    }
  }

  // Determinism (P1a). Under a concurrency pool the completion order is a race,
  // and `failures` feeds preflight findings which feed export-report.html — a
  // file the golden baseline byte-compares. Sorting by URL makes the report a
  // function of the project rather than of the scheduler.
  failures.sort((a, b) => (a.url < b.url ? -1 : a.url > b.url ? 1 : 0));

  // Final progress. The pre-P1a callback fired BEFORE each item, so a caller
  // rendering "12/60" was always one behind and never saw 60/60.
  if (onProgress) onProgress({ total, completed, current: null });

  return { resolvedMap, warnings, failures };
}
